import { KeyboardEvent, ReactNode, forwardRef } from 'react'
import { ButtonType, IconPaths, IconSize, KTSVG } from '../../../_metronic/helpers'
import styles from './styles/Button.module.css'

/** Variantes do padrão novo; sem variant, o visual vem das classes em `className` (btn-primary etc.). */
export type ButtonVariant = 'secondary-outline' | 'danger-outline' | 'tertiary'

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  'secondary-outline': styles.secondaryOutline,
  'danger-outline': styles.dangerOutline,
  tertiary: styles.tertiary,
}

type IButton = {
  variant?: ButtonVariant
  onClick?: () => void
  children?: ReactNode
  icon?: IconPaths | null
  iconSize?: IconSize
  type?: ButtonType
  className?: string
  disabled?: boolean
  size?: 'btn-lg' | 'btn-sm'
  id?: string
  title?: string // permite tooltip com nome completo
  /** Nome acessível quando o texto visível não basta (ex.: "Alterar Hospital X"). */
  ariaLabel?: string
  /** Para botões que mostram/escondem uma região (ex.: "Mais filtros"). */
  ariaExpanded?: boolean
  ariaControls?: string
  /** Texto que descreve o botão além do nome (ex.: o motivo de estar bloqueado). */
  ariaDescribedBy?: string
  /**
   * Bloqueado sem sair da ordem do Tab: continua focável e clicável (o `onClick` decide o que
   * fazer, ex.: levar à pendência). Use `disabled` quando o botão deve sumir do teclado.
   */
  ariaDisabled?: boolean
  /** Botão de alternância (chip de filtro, liga/desliga). */
  ariaPressed?: boolean
  /** Papel ARIA quando o botão faz parte de um widget composto (ex.: `tab` num `tablist`). */
  role?: 'tab'
  /** Com `role='tab'`: a aba ativa. */
  ariaSelected?: boolean
  /** Foco gerenciado por teclado (ex.: só a aba ativa entra no Tab, `-1` nas demais). */
  tabIndex?: number
  onKeyDown?: (evento: KeyboardEvent<HTMLButtonElement>) => void
  /**
   * Sem as classes do Bootstrap (`btn`, centralizado, `text-truncate`) e sem o invólucro do texto:
   * o `className` decide todo o visual. Para cabeçalhos clicáveis, linhas de lista e afins, onde
   * o conteúdo tem layout próprio, mas o elemento continua sendo um botão.
   */
  unstyled?: boolean
  /** Envia um formulário que não é ancestral do botão (ex.: rodapé fixo fora do `<form>`). */
  form?: string
}

export const Button = forwardRef<HTMLButtonElement, IButton>(
  (
    {
      children,
      onClick,
      icon = null,
      type = ButtonType.Button,
      className = '',
      iconSize = IconSize.ExtraSmall,
      disabled = false,
      size = '',
      id,
      title,
      variant,
      ariaLabel,
      ariaExpanded,
      ariaControls,
      ariaDescribedBy,
      ariaDisabled,
      ariaPressed,
      role,
      ariaSelected,
      tabIndex,
      onKeyDown,
      unstyled = false,
      form,
    },
    ref
  ) => {
    const iconElement = icon ? (
      <span className={`${unstyled ? '' : 'me-2 '}d-flex align-items-center`}>
        <KTSVG path={icon} size={iconSize} />
      </span>
    ) : null

    return (
      <button
        ref={ref}
        id={id}
        type={type}
        form={form}
        className={
          unstyled
            ? className
            : `d-flex btn align-items-center justify-content-center text-truncate ${
                // btn-outline: impede o Metronic de zerar a borda (ver Button.module.css).
                variant ? `btn-outline ${VARIANT_CLASS[variant]}` : ''
              } ${className} ${size}`
        }
        onClick={onClick}
        onKeyDown={onKeyDown}
        disabled={disabled}
        role={role}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        aria-describedby={ariaDescribedBy}
        aria-disabled={ariaDisabled}
        aria-pressed={ariaPressed}
        aria-selected={ariaSelected}
        title={title ?? (typeof children === 'string' ? children : undefined)}
      >
        {iconElement}
        {unstyled ? children : <span className='text-truncate'>{children}</span>}
      </button>
    )
  }
)
Button.displayName = 'Button'
